import { CalendarCheck, CalendarPlus, Home, Inbox, Sparkles } from 'lucide-react';
import { Link } from 'react-router';

import { ClientJobCard } from '@/components/client/ClientJobCard';
import { ClientPage } from '@/components/client/ClientPage';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { KpiCard } from '@/components/ui/kpi-card';
import { useClientDashboard } from '@/hooks/client-portal';
import { formatDay } from '@/lib/dates';
import { useSession } from '@/lib/auth-client';

/*
 * /portal — ROADMAP.md §5.1.
 *
 * KPI row, the next few cleans, and a profile summary. Three rules from
 * design-system.md §7 are visible on this one screen:
 *
 *   - **Zero is `0`.** `KpiCard` cannot render a dash for a number; a client
 *     with no cleans yet has zero cleans, and that is a fact worth showing.
 *   - **A failed request is an error**, never an empty state (P22). The three
 *     branches below are loading, error and empty — and they are different.
 *   - **Absent values say why.** "Not on file", italic — never a bare em-dash.
 */

export function ClientDashboardPage() {
  const { data: session } = useSession();
  const dashboard = useClientDashboard();

  const firstName = session?.user.name.trim().split(' ')[0] ?? '';
  const data = dashboard.data;

  return (
    <ClientPage
      title={firstName ? `Hello, ${firstName}` : 'Hello'}
      description="Your cleans, your properties, and anything you have asked us for."
      // No "Request a Cleaning" action here. The topbar carries it on every
      // page, and two identical buttons an inch apart is defect P2 in a new
      // costume — design-system.md §8, never render the same control twice.
    >
      {dashboard.error ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="We could not load your account"
            hint="Something went wrong on our side — this is not a problem with your account. Try again, and ring the office if it keeps happening."
            onRetry={() => {
              void dashboard.refetch();
            }}
          />
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              label="Upcoming cleans"
              value={data?.counts.upcoming ?? null}
              icon={CalendarCheck}
              isLoading={dashboard.isPending}
              subLabel="Booked and still to come"
            />
            <KpiCard
              label="Cleans completed"
              value={data?.counts.completed ?? null}
              icon={Sparkles}
              isLoading={dashboard.isPending}
              subLabel="Since you joined us"
            />
            <KpiCard
              label="Waiting on us"
              value={data?.counts.openRequests ?? null}
              icon={Inbox}
              isLoading={dashboard.isPending}
              subLabel={
                <Link to="/portal/request" className="underline underline-offset-2">
                  Requests we have not answered
                </Link>
              }
            />
            <KpiCard
              label="Properties"
              value={data?.counts.properties ?? null}
              icon={Home}
              isLoading={dashboard.isPending}
              subLabel={
                <Link to="/portal/properties" className="underline underline-offset-2">
                  Manage your addresses
                </Link>
              }
            />
          </div>

          <section className="mt-8">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-h2 text-ink-900">Coming up</h2>
              <Link
                to="/portal/cleans"
                className="text-label text-bark-700 underline-offset-2 hover:underline"
              >
                See every clean
              </Link>
            </div>

            {dashboard.isPending ? (
              <ul aria-busy="true" className="space-y-3">
                <span className="sr-only">Loading your upcoming cleans</span>
                {[0, 1].map((key) => (
                  <li
                    key={key}
                    aria-hidden="true"
                    className="h-[164px] animate-pulse rounded-lg bg-ink-100"
                  />
                ))}
              </ul>
            ) : data && data.upcoming.length > 0 ? (
              <ul className="space-y-3">
                {data.upcoming.map((job) => (
                  <ClientJobCard key={job.id} job={job} />
                ))}
              </ul>
            ) : (
              <div className="rounded-lg border border-ink-300 bg-white shadow-card">
                <EmptyState
                  icon={CalendarPlus}
                  title="Nothing booked at the moment."
                  hint="Ask us for a clean and we will confirm a date with you."
                  action={
                    <Button asChild>
                      <Link to="/portal/request">
                        <CalendarPlus aria-hidden="true" />
                        Request a Cleaning
                      </Link>
                    </Button>
                  }
                />
              </div>
            )}
          </section>

          <section className="mt-8">
            <h2 className="mb-3 text-h2 text-ink-900">Your details</h2>
            <div className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
              {dashboard.isPending ? (
                <div aria-hidden="true" className="h-24 animate-pulse rounded-sm bg-ink-100" />
              ) : data ? (
                <>
                  <dl className="grid gap-4 sm:grid-cols-2">
                    <Detail label="Account">{data.profile.displayName}</Detail>
                    <Detail label="Contact">{data.profile.contactName}</Detail>
                    <Detail label="Email" absent="Not on file">
                      {data.profile.email}
                    </Detail>
                    <Detail label="Phone" absent="Not on file">
                      {data.profile.phone}
                    </Detail>
                    <Detail label="Main address" absent="No property saved yet">
                      {data.primaryAddress}
                    </Detail>
                    <Detail label="Customer since" absent="Not recorded">
                      {data.profile.customerSince ? formatDay(data.profile.customerSince) : null}
                    </Detail>
                  </dl>
                  <p className="mt-4 text-meta text-ink-500">
                    To change your contact details, ring the office on (435) 238-3286 and a team
                    member will update your account.{' '}
                    <Link
                      to="/portal/profile"
                      className="text-bark-700 underline underline-offset-2"
                    >
                      See your full profile
                    </Link>
                  </p>
                </>
              ) : null}
            </div>
          </section>
        </>
      )}
    </ClientPage>
  );
}

/**
 * One labelled fact.
 *
 * `absent` is required for anything that can be missing and has to be a
 * sentence, not a dash — design-system.md §7, and the pattern this portal is
 * supposed to be teaching the rest of the app.
 */
function Detail({
  label,
  absent,
  children,
}: {
  label: string;
  absent?: string;
  children: string | null | undefined;
}) {
  return (
    <div>
      <dt className="text-meta text-ink-500">{label}</dt>
      <dd className="mt-0.5 text-body text-ink-900">
        {children ?? <span className="italic text-ink-500">{absent ?? 'Not on file'}</span>}
      </dd>
    </div>
  );
}
